import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Typography, Button, CircularProgress, Dialog, IconButton, InputBase, Collapse, Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import SearchIcon from "@mui/icons-material/Search";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RefreshIcon from "@mui/icons-material/Refresh";
import GroupsIcon from "@mui/icons-material/Groups";
import moment from "moment";
import SessionUpcoming from "./SessionUpcoming";
import SessionAmend from "./SessionAmend";
import BookingActions from "./BookingActions";
import {
  get_session_api, generate_session_recap_api, set_session_todo_api,
} from "../../../DAL/SchoolPortal/Tutors";
import {
  border, textPrimary, textSecondary, textMuted, accent, font,
  success, warning, infoBlue, secondaryButtonSx,
  filledButtonSx, ACTION_COLORS,
} from "../../../layouts/schoolPortal/schoolPortalTheme";

/**
 * ONE SESSION, ENTIRELY.
 *
 * WHAT THIS REPLACES
 * A drawer that showed a video, a debrief and a consent line. Everything else
 * the platform knows about a session — what was said, what it covered, who was
 * in the room, what anybody agreed to do next — was either unreachable or
 * lived on another screen. A session page you have to leave in order to find
 * out what happened in the session is not a session page.
 *
 * THE ORDER IS THE ANSWER ORDER, NOT THE DATA ORDER.
 * Someone opening a past session wants, in this order: what happened (the
 * recap), what I owe (the to-dos and the debrief), then the evidence (the
 * recording, the transcript). The evidence is last and the transcript starts
 * collapsed, because nine thousand words above the summary buries it — and the
 * summary is the thing that makes the transcript worth having.
 *
 * AN UPCOMING SESSION IS A DIFFERENT PAGE WEARING THE SAME FRAME.
 * There is nothing to summarise and nothing to play; what exists is who, when,
 * and what they asked for. Rendering the empty shells of the past-session
 * sections for it would be five "not yet" panels stacked above the one line
 * that matters.
 *
 * THE TRANSCRIPT DRIVES THE VIDEO. Every turn carries its timestamp and
 * clicking one seeks the player, because "show me the bit where we covered
 * that" is the only reason anyone opens a fifty-minute recording, and scrubbing
 * for it by hand is why nobody does.
 */

const H = { fontFamily: font };

const sectionLabel = {
  ...H, fontSize: "10px", fontWeight: 800, letterSpacing: "0.08em",
  textTransform: "uppercase", color: textMuted, mb: 0.8,
};

/**
 * Why a session has no recording.
 *
 * The consent wordings are kept for HISTORY, not for anything new: sessions are
 * recorded as a matter of policy now and nobody is asked
 * (services/recordingConsentService.js), so `someone_declined` and
 * `consent_not_given` can only be attached to sessions that ran under the old
 * default-deny rule. Deleting them would relabel those rows with a reason that
 * was not the real one; the copy just no longer implies a question that could
 * have been answered differently today.
 */
const NO_RECORDING = {
  someone_declined: "Not recorded. This session ran before recording became automatic.",
  not_recorded: "This session was not recorded.",
  consent_not_given: "Not recorded. This session ran before recording became automatic.",
  failed: "The recording could not be saved after the call.",
};

const NO_TRANSCRIPT = {
  none: "No transcript for this session.",
  transcribing: "The transcript is still being produced. It usually lands within an hour of the call.",
  failed: "This session could not be transcribed.",
};

const OWNER_LABEL = { student: "Student", instructor: "You", both: "Both" };

const prettySubject = (s) =>
  s ? s.replace(/-/g, " ").replace(/^\w/, (c) => c.toUpperCase()) : null;

const money = (n, c) =>
  c ? new Intl.NumberFormat(undefined, { style: "currency", currency: c, maximumFractionDigits: 0 }).format(n || 0) : null;

const clock = (secs) => {
  if (!Number.isFinite(secs)) return null;
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

/** A titled block. Every section is one, so nothing can drift out of rhythm. */
function Section({ label, children, right }) {
  return (
    <Box sx={{ mb: 2.25 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography sx={{ ...sectionLabel, mb: 0.8, flex: 1 }}>{label}</Typography>
        {right}
      </Box>
      {children}
    </Box>
  );
}

export default function SessionDetail({ id, onClose, onDebrief, onPrep, asPage = false, onExpand, onJoin, region }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [recapBusy, setRecapBusy] = useState(false);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [tq, setTq] = useState("");
  const video = useRef(null);

  const load = useCallback(async (sessionId) => {
    setState({ loading: true, data: null, error: null });
    const r = await get_session_api(sessionId);
    if (r?.code === 200) setState({ loading: false, data: r, error: null });
    else setState({ loading: false, data: null, error: r?.message || "Could not load that session" });
  }, []);

  useEffect(() => {
    if (!id) return;
    setTranscriptOpen(false);
    setTq("");
    load(id);
  }, [id, load]);

  const s = state.data?.session;
  const rec = state.data?.recording;
  const tr = state.data?.transcript;
  const recap = state.data?.recap;
  const consent = state.data?.consent || [];
  const isClass = s?.kind === "class";

  /**
   * Regenerate is a different word from summarise and does a different thing,
   * so it sends a different flag. Without `force` the server correctly returns
   * the recap it already has, and the button would do nothing while appearing
   * to work.
   */
  const runRecap = async (force) => {
    setRecapBusy(true);
    const r = await generate_session_recap_api(id, force);
    if (r?.code === 200) setState((p) => ({ ...p, data: { ...p.data, recap: r.recap } }));
    else setState((p) => ({ ...p, data: { ...p.data, recap: { ...(p.data?.recap || {}), status: "failed", failureReason: r?.message } } }));
    setRecapBusy(false);
  };

  /**
   * Ticked immediately, reconciled with the server's answer.
   *
   * A checkbox that waits for a round trip before moving reads as broken, and
   * this list is ticked several at a time. The server's recap replaces the
   * local one on success, so a rejected write (a stale index after a
   * regenerate) corrects itself rather than leaving a lie on the screen.
   */
  const toggleTodo = async (item) => {
    const next = !item.done;
    setState((p) => ({
      ...p,
      data: {
        ...p.data,
        recap: {
          ...p.data.recap,
          actionItems: p.data.recap.actionItems.map((a) => (a.index === item.index ? { ...a, done: next } : a)),
        },
      },
    }));
    const r = await set_session_todo_api(id, item.index, next, item.text);
    if (r?.code === 200) setState((p) => ({ ...p, data: { ...p.data, recap: r.recap } }));
    else load(id);
  };

  const seek = (at) => {
    if (!video.current || !Number.isFinite(at)) return;
    video.current.currentTime = at;
    video.current.play?.().catch(() => {});
    video.current.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
  };

  const turns = useMemo(() => {
    const all = tr?.turns || [];
    if (!tq.trim()) return all;
    const needle = tq.trim().toLowerCase();
    return all.filter((t) => t.text.toLowerCase().includes(needle));
  }, [tr, tq]);

  /**
   * PAGE OR PANEL, SAME CONTENT.
   *
   * THE PANEL IS THE PAGE, SMALLER. Clicking a block on the calendar opens
   * the panel — a glance, without losing the week you were looking at.
   * Opening from the bay or the ledger goes to the page. Neither is a
   * different screen, so anyone who starts with a glance and finds they need
   * the whole thing expands rather than going back to hunt for another way
   * in.
   *
   * A session has a URL now — /school/sessions/:id — because a specific
   * session is a specific thing and was only ever reachable by finding the
   * row that produced it. You could not send a colleague "look at this one",
   * could not bookmark the lesson you are about to teach, and the browser
   * back button did not know the panel had opened.
   *
   * The panel stays for opening one from inside a list without losing your
   * place in it. Only the frame differs; nothing below this line knows which
   * it is in, so the two cannot drift apart.
   */
  /**
   * THE HEADER CARRIES THE STATE AS COLOUR.
   *
   * Every session looked identical until you read the pills: a cancelled one,
   * a live one and a lesson in March opened the same white box. The band is
   * the same vocabulary the rest of the product uses — green when the room is
   * open, purple for a class, amber when a debrief is owed, grey when it is
   * cancelled — so the page announces what it is before anybody reads a word
   * of it.
   */
  const band = !s ? border
    : s.status === "cancelled" ? textMuted
      : s.join?.open ? ACTION_COLORS.join
        : s.needsDebrief || (!s.upcoming && !s.summary?.body) ? ACTION_COLORS.debrief
          : isClass ? ACTION_COLORS.klass
            : accent;

  const body = (
      <Box sx={{ ...(asPage ? { maxWidth: 900, mx: "auto" } : {}) }}>
        <Box sx={{ height: 4, backgroundColor: band, flexShrink: 0 }} />
      <Box sx={{ p: 2.25 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
              {isClass && <GroupsIcon sx={{ fontSize: 18, color: textMuted }} />}
              <Typography sx={{ ...H, fontSize: "18px", fontWeight: 800, color: textPrimary }}>
                {s ? (isClass ? s.title : s.student) : "Session"}
              </Typography>
            </Box>
            {s && (
              <Typography sx={{ ...H, fontSize: "12.5px", color: textSecondary, mt: 0.25 }}>
                {moment(s.startedAt).format("ddd D MMM YYYY, HH:mm")} · {s.minutes} min
                {s.instructor ? ` · ${s.instructor}` : ""}
              </Typography>
            )}
            {s && (
              <Box sx={{ display: "flex", gap: 0.6, mt: 0.8, flexWrap: "wrap" }}>
                {s.status === "cancelled" && <Pill text="Cancelled" color={textMuted} />}
                {s.upcoming && s.status !== "cancelled" && <Pill text={moment(s.startedAt).fromNow()} color={accent} />}
                {[prettySubject(s.subject), s.lesson ? `Lesson ${s.lesson}` : null]
                  .filter(Boolean).map((t) => <Pill key={t} text={t} color={textSecondary} />)}
                {isClass && <Pill text={`${s.seats} of ${s.capacity} booked`} color={textSecondary} />}
                {money(s.amount, s.currency) && <Pill text={money(s.amount, s.currency)} color={textSecondary} />}
              </Box>
            )}
          </Box>
          {/* WHAT YOU CAN DO TO THIS SESSION, AT THE TOP.
              It used to be an unlabelled dot inside "Change this session",
              three sections down — an instructor looking for cancel there
              reported, correctly, that there was no way to cancel their own
              lesson. Cancel is the only destructive thing on this page and is
              the only thing in that colour. */}
          {s && s.status !== "cancelled" && !isClass && (
            <Box sx={{ flexShrink: 0, mr: 1 }}>
              <BookingActions
                inline
                booking={{
                  _id: s.id,
                  status: s.status,
                  start_time: s.startedAt,
                  // An upcoming session has no endedAt — that is stamped when
                  // it runs — so it comes from the scheduled length.
                  end_time: s.endedAt
                    || (s.startedAt && s.minutes
                      ? new Date(new Date(s.startedAt).getTime() + s.minutes * 60000).toISOString()
                      : null),
                  location_type: s.locationType,
                  subject: s.subject,
                }}
                canTeach
                onDone={() => { load(id); onClose?.(); }}
              />
            </Box>
          )}
          {!asPage && onExpand && (
            <Tooltip title="Open as a page" placement="left">
              <IconButton size="small" onClick={() => onExpand(id)}>
                <OpenInFullIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
          {!asPage && <IconButton size="small" onClick={onClose}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>}
        </Box>

        {state.loading && <Box sx={{ py: 6, textAlign: "center" }}><CircularProgress size={22} sx={{ color: accent }} /></Box>}
        {state.error && <Typography sx={{ ...H, fontSize: "12.5px", color: warning }}>{state.error}</Typography>}

        {s && s.upcoming && (
          <>
            {/* THE BRIEF IS ON THE PAGE, NOT BEHIND A BUTTON. It used to open a
                SECOND dialog on top of this one — a page about a session that
                you had to leave in order to learn anything about the session.
                A click is enough friction to stop it being read at all when
                the session starts in four minutes. */}
            <SessionUpcoming session={s} gate={state.data?.gate} consent={state.data?.consent} onJoin={onJoin} />
            {isClass && <Attendees list={s.attendees} />}
            {/* Managing it is here too, for the same reason. Reading about a
                session you cannot change is a status page. */}
            {s.status !== "cancelled" && (
              <SessionAmend
                booking={{ _id: s.id, subject: s.subject, lesson: s.lesson, topic: s.topic }}
                onDone={() => { load(id); onClose?.(); }}
              />
            )}
            <Typography sx={{ ...H, fontSize: "12px", color: textMuted, mt: 2 }}>
              The recording, transcript and recap appear here after the session runs.
            </Typography>
          </>
        )}

        {s && !s.upcoming && (
          <>
            {/* ── WHAT HAPPENED ─────────────────────────────────────────── */}
            <Section
              label="Recap"
              right={recap?.status === "ready" && (
                <Tooltip title="Build it again from the transcript">
                  <span>
                    <IconButton size="small" disabled={recapBusy} onClick={() => runRecap(true)}>
                      <RefreshIcon sx={{ fontSize: 16, color: textMuted }} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            >
              <RecapBody
                recap={recap}
                transcriptStatus={tr?.status}
                busy={recapBusy}
                onGenerate={() => runRecap(false)}
                onToggleTodo={toggleTodo}
              />
            </Section>

            {/* ── WHAT YOU OWE ──────────────────────────────────────────── */}
            {!isClass && (
              <Section label="Debrief">
                {s.summary?.body ? (
                  <>
                    <Typography sx={{ ...H, fontSize: "13px", color: textPrimary, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                      {s.summary.body}
                    </Typography>
                    <Typography sx={{ ...H, fontSize: "11px", color: textMuted, mt: 0.6 }}>
                      {s.summary.approved ? "Approved — the student can read this." : "Draft — the student cannot see it until it is approved."}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography sx={{ ...H, fontSize: "12.5px", color: warning, fontWeight: 600, mb: 1 }}>
                      {s.lesson
                        ? `No debrief yet — Lesson ${s.lesson + 1} stays locked until there is one.`
                        : "No debrief yet — their follow-up work goes out once there is one."}
                    </Typography>
                    <Button
                      onClick={() => onDebrief?.(id)}
                      // The same act wears the same colour it wears in the bay,
                      // the ledger and the calendar hatch.
                      sx={filledButtonSx(ACTION_COLORS.debrief)}
                    >
                      Write the debrief
                    </Button>
                    {recap?.status === "ready" && recap.summary && (
                      <Typography sx={{ ...H, fontSize: "11.5px", color: textMuted, mt: 0.8 }}>
                        The recap above is a draft you can start from. It is never published to the student on its own.
                      </Typography>
                    )}
                  </>
                )}
              </Section>
            )}

            {isClass && <Attendees list={s.attendees} />}

            {/* ── THE EVIDENCE ──────────────────────────────────────────── */}
            <Section label="Recording">
              <Box sx={{ border: `1px solid ${border}`, borderRadius: "10px", overflow: "hidden", backgroundColor: "#000" }}>
                {rec?.available && rec.playback?.url ? (
                  <video
                    ref={video}
                    src={rec.playback.url}
                    controls
                    style={{ width: "100%", display: "block", maxHeight: 300, background: "#000" }}
                  />
                ) : (
                  <Box sx={{ p: 2.5, textAlign: "center", backgroundColor: "#fff" }}>
                    <PlayCircleOutlineIcon sx={{ fontSize: 26, color: textMuted }} />
                    <Typography sx={{ ...H, fontSize: "12.5px", color: textSecondary, mt: 0.5 }}>
                      {rec?.error
                        ? "The recording is stored but could not be opened just now."
                        : NO_RECORDING[rec?.reason] || "No recording for this session."}
                    </Typography>
                  </Box>
                )}
              </Box>
              {rec?.available && (
                <Typography sx={{ ...H, fontSize: "11px", color: textMuted, mt: 0.6 }}>
                  {rec.durationSeconds ? `${Math.round(rec.durationSeconds / 60)} min recorded · ` : ""}
                  This link is signed for one hour — reopen the session for a fresh one.
                </Typography>
              )}
            </Section>

            <Section label="Transcript">
              {tr?.status !== "ready" ? (
                <Typography sx={{ ...H, fontSize: "12.5px", color: textSecondary }}>
                  {NO_TRANSCRIPT[tr?.status] || NO_TRANSCRIPT.none}
                </Typography>
              ) : (
                <>
                  {tr.talk && (
                    <Box sx={{ mb: 1.25 }}>
                      <Box sx={{ display: "flex", height: 6, borderRadius: "999px", overflow: "hidden", mb: 0.6 }}>
                        {tr.talk.speakers.map((sp, i) => (
                          <Box key={sp.label} sx={{ width: `${sp.share}%`, backgroundColor: i === 0 ? accent : infoBlue }} />
                        ))}
                      </Box>
                      <Typography sx={{ ...H, fontSize: "11.5px", color: textSecondary }}>
                        {tr.talk.speakers.map((sp) => `${sp.label} ${sp.share}%`).join(" · ")}
                        {/* Said out loud when the labels are guesses. Presenting
                            "Speaker 1" as the instructor would be a wrong
                            attribution somebody quotes back at a person. */}
                        {tr.talk.speakers.some((sp) => sp.role === "unknown") ? " · speakers not identified" : ""}
                      </Typography>
                    </Box>
                  )}

                  <Box
                    onClick={() => setTranscriptOpen((v) => !v)}
                    sx={{ display: "flex", alignItems: "center", gap: 0.6, cursor: "pointer", userSelect: "none", mb: transcriptOpen ? 1 : 0 }}
                  >
                    <ExpandMoreIcon sx={{
                      fontSize: 18, color: textSecondary,
                      transform: transcriptOpen ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform .15s",
                    }} />
                    <Typography sx={{ ...H, fontSize: "12.5px", fontWeight: 700, color: textPrimary }}>
                      {transcriptOpen ? "Hide" : "Read"} the transcript
                    </Typography>
                    <Typography sx={{ ...H, fontSize: "11.5px", color: textMuted }}>
                      {tr.words ? `${tr.words.toLocaleString()} words` : `${tr.turnsTotal} turns`}
                    </Typography>
                  </Box>

                  <Collapse in={transcriptOpen} mountOnEnter>
                    <Box sx={{
                      display: "flex", alignItems: "center", gap: 0.8, px: 1.1, py: 0.5, mb: 1,
                      border: `1px solid ${border}`, borderRadius: "9px", backgroundColor: "#fff",
                    }}>
                      <SearchIcon sx={{ fontSize: 16, color: textMuted }} />
                      <InputBase
                        value={tq}
                        onChange={(e) => setTq(e.target.value)}
                        placeholder="Find a word in this session…"
                        sx={{ ...H, fontSize: "12.5px", flex: 1 }}
                      />
                      {tq && (
                        <Typography sx={{ ...H, fontSize: "11px", color: textMuted, whiteSpace: "nowrap" }}>
                          {turns.length} of {tr.turns.length}
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{
                      maxHeight: 380, overflowY: "auto", border: `1px solid ${border}`,
                      borderRadius: "10px", backgroundColor: "#fff", p: 1.25,
                    }}>
                      {!turns.length ? (
                        <Typography sx={{ ...H, fontSize: "12.5px", color: textSecondary, py: 2, textAlign: "center" }}>
                          Nothing in this session matches that.
                        </Typography>
                      ) : turns.map((t, i) => (
                        <Box key={i} sx={{ display: "flex", gap: 1, py: 0.55, alignItems: "flex-start" }}>
                          <Typography
                            onClick={() => rec?.available && seek(t.at)}
                            sx={{
                              ...H, fontSize: "11px", color: rec?.available && t.at != null ? accent : textMuted,
                              fontVariantNumeric: "tabular-nums", width: 40, flexShrink: 0, pt: "1px",
                              cursor: rec?.available && t.at != null ? "pointer" : "default",
                              "&:hover": rec?.available && t.at != null ? { textDecoration: "underline" } : {},
                            }}
                          >
                            {clock(t.at) || "—"}
                          </Typography>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ ...H, fontSize: "10.5px", fontWeight: 800, color: textSecondary, letterSpacing: "0.04em" }}>
                              {t.speaker}
                            </Typography>
                            <Typography sx={{ ...H, fontSize: "12.5px", color: textPrimary, lineHeight: 1.55 }}>
                              {t.text}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                      {tr.truncated && !tq && (
                        <Typography sx={{ ...H, fontSize: "11.5px", color: textMuted, pt: 1, borderTop: `1px solid ${border}`, mt: 1 }}>
                          Showing the first {tr.turns.length} of {tr.turnsTotal} turns.
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                </>
              )}
            </Section>

            {(tr?.acsCodes?.length > 0 || s.acsCodes?.length > 0) && (
              <Section label="Covered">
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  {(tr?.acsCodes?.length ? tr.acsCodes : s.acsCodes).map((c) => (
                    <Pill key={c} text={c} color={infoBlue} />
                  ))}
                </Box>
                <Typography sx={{ ...H, fontSize: "11px", color: textMuted, mt: 0.7 }}>
                  {tr?.acsCodes?.length
                    ? "Matched against the words actually spoken, so this under-reports rather than over-reports."
                    : "What this class was scheduled to cover."}
                </Typography>
              </Section>
            )}

            {consent.length > 0 && (
              <Section label="Recording consent">
                {consent.map((c, i) => (
                  <Typography key={i} sx={{ ...H, fontSize: "12px", color: textSecondary }}>
                    {c.role === "instructor" ? "You" : "Student"} — {c.granted ? "agreed" : "declined"}
                    {c.at ? ` ${moment(c.at).format("D MMM YYYY")}` : ""}
                  </Typography>
                ))}
              </Section>
            )}
          </>
        )}
      </Box>
      </Box>
  );

  if (asPage) return body;
  return (
    // A DIALOG, NOT A DRAWER. This slid in from the right, over the right-hand
    // strip bay — a sidebar covering a sidebar, at sidebar width, for the
    // densest surface in the product. A session is the thing you are looking
    // at when you open it, so it takes the middle of the screen rather than
    // squeezing into a column beside whatever you were doing before.
    <Dialog
      open={Boolean(id)}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      PaperProps={{ sx: { borderRadius: "14px", backgroundColor: "#FAF8F5", overflow: "hidden" } }}
    >
      {body}
    </Dialog>
  );
}

function Pill({ text, color }) {
  return (
    <Typography sx={{
      ...H, fontSize: "10.5px", fontWeight: 700, color,
      border: `1px solid ${color}33`, backgroundColor: `${color}12`,
      px: 0.8, py: 0.25, borderRadius: "999px", whiteSpace: "nowrap",
    }}>
      {text}
    </Typography>
  );
}

function Attendees({ list }) {
  if (!list?.length) return null;
  const booked = list.filter((a) => a.state === "booked" || a.state === "attended");
  const others = list.filter((a) => !booked.includes(a));
  return (
    <Section label="Who was on it">
      {booked.map((a, i) => (
        <Typography key={i} sx={{ ...H, fontSize: "12.5px", color: textPrimary, py: 0.15 }}>
          {a.name}
          {a.state === "attended" ? " · attended" : ""}
        </Typography>
      ))}
      {/* Cancellations and no-shows stay visible. A class list that shows only
          the people who turned up cannot answer "did Priya ever take this?" —
          which is what it is opened with when someone is chasing an absence. */}
      {others.map((a, i) => (
        <Typography key={`o${i}`} sx={{ ...H, fontSize: "12.5px", color: textMuted, py: 0.15 }}>
          {a.name} · {a.state === "waitlisted" ? "waitlisted" : a.state === "no_show" ? "did not attend" : "cancelled"}
        </Typography>
      ))}
    </Section>
  );
}

/**
 * The recap, or an honest account of why there isn't one.
 *
 * FOUR ABSENCES, FOUR SENTENCES. "Still transcribing", "never transcribed",
 * "not summarised yet" and "the summary failed" ask the reader to do four
 * different things — wait, stop waiting, press a button, press it again. One
 * "unavailable" for all of them leaves somebody refreshing a session that
 * failed an hour ago.
 */
function RecapBody({ recap, transcriptStatus, busy, onGenerate, onToggleTodo }) {
  const status = recap?.status || "none";

  if (busy || status === "generating") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
        <CircularProgress size={16} sx={{ color: accent }} />
        <Typography sx={{ ...H, fontSize: "12.5px", color: textSecondary }}>Reading the transcript…</Typography>
      </Box>
    );
  }

  if (status === "transcribing" || transcriptStatus === "transcribing") {
    return (
      <Typography sx={{ ...H, fontSize: "12.5px", color: textSecondary }}>
        The transcript is still being produced. The recap is written automatically as soon as it lands.
      </Typography>
    );
  }

  if (status === "no_transcript" || transcriptStatus === "none" || transcriptStatus === "failed") {
    return (
      <Typography sx={{ ...H, fontSize: "12.5px", color: textSecondary }}>
        There is no transcript for this session, so there is nothing to summarise.
      </Typography>
    );
  }

  if (status === "failed") {
    return (
      <Box>
        <Typography sx={{ ...H, fontSize: "12.5px", color: warning, mb: 1 }}>
          The recap could not be written{recap?.failureReason ? ` — ${recap.failureReason}` : ""}.
        </Typography>
        <Button onClick={onGenerate} startIcon={<RefreshIcon sx={{ fontSize: 15 }} />}
          sx={{ ...secondaryButtonSx, minHeight: 0, py: 0.6, px: 1.4, fontSize: "12.5px" }}>
          Try again
        </Button>
      </Box>
    );
  }

  if (status !== "ready") {
    return (
      <Button onClick={onGenerate} startIcon={<AutoAwesomeIcon sx={{ fontSize: 15 }} />}
        sx={{ ...secondaryButtonSx, minHeight: 0, py: 0.6, px: 1.4, fontSize: "12.5px" }}>
        Summarise this session
      </Button>
    );
  }

  return (
    <Box>
      {recap.summary && (
        <Typography sx={{ ...H, fontSize: "13px", color: textPrimary, lineHeight: 1.65, whiteSpace: "pre-wrap" }}>
          {recap.summary}
        </Typography>
      )}

      {recap.keyPoints?.length > 0 && (
        <Box sx={{ mt: 1.4 }}>
          {recap.keyPoints.map((k, i) => (
            <Box key={i} sx={{ display: "flex", gap: 0.9, py: 0.25 }}>
              <Box sx={{ width: 4, height: 4, borderRadius: "50%", backgroundColor: textMuted, mt: "8px", flexShrink: 0 }} />
              <Typography sx={{ ...H, fontSize: "12.5px", color: textPrimary, lineHeight: 1.55 }}>{k}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {recap.weakAreas?.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography sx={{ ...sectionLabel, mb: 0.5 }}>Struggled with</Typography>
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {recap.weakAreas.map((w) => <Pill key={w} text={w} color={warning} />)}
          </Box>
        </Box>
      )}

      {recap.actionItems?.length > 0 && (
        <Box sx={{ mt: 1.6 }}>
          <Typography sx={{ ...sectionLabel, mb: 0.5 }}>Follow-ups</Typography>
          {recap.actionItems.map((a) => (
            <Box
              key={a.index}
              onClick={() => onToggleTodo(a)}
              sx={{
                display: "flex", alignItems: "flex-start", gap: 0.9, py: 0.4, cursor: "pointer",
                "&:hover .todo-text": { color: textPrimary },
              }}
            >
              {a.done
                ? <CheckCircleIcon sx={{ fontSize: 17, color: success, flexShrink: 0, mt: "1px" }} />
                : <RadioButtonUncheckedIcon sx={{ fontSize: 17, color: textMuted, flexShrink: 0, mt: "1px" }} />}
              <Typography className="todo-text" sx={{
                ...H, fontSize: "12.5px", lineHeight: 1.55, flex: 1,
                color: a.done ? textMuted : textPrimary,
                textDecoration: a.done ? "line-through" : "none",
              }}>
                {a.text}
              </Typography>
              <Typography sx={{ ...H, fontSize: "10.5px", fontWeight: 700, color: textMuted, flexShrink: 0, pt: "2px" }}>
                {OWNER_LABEL[a.owner] || ""}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {recap.nextFocus && (
        <Box sx={{ mt: 1.6, p: 1.2, borderRadius: "9px", backgroundColor: `${accent}0d`, border: `1px solid ${accent}22` }}>
          <Typography sx={{ ...sectionLabel, mb: 0.35, color: accent }}>Start the next one with</Typography>
          <Typography sx={{ ...H, fontSize: "12.5px", color: textPrimary, lineHeight: 1.55 }}>{recap.nextFocus}</Typography>
        </Box>
      )}

      <Typography sx={{ ...H, fontSize: "10.5px", color: textMuted, mt: 1.4 }}>
        {/* Provenance, because a reader who does not know a summary was
            machine-written cannot weigh it. */}
        Written from the transcript{recap.generatedAt ? ` ${moment(recap.generatedAt).fromNow()}` : ""} — check it before you act on it.
      </Typography>
    </Box>
  );
}
